import { useCallback, useEffect, useState } from 'react';
import pushService from '../services/pushService';

/**
 * Where push stands on this device, and the three things you can do about it.
 *
 * state: 'loading' · 'subscribed' · 'default' (not yet) · 'denied' (browser blocked) ·
 *        'unsupported' · 'ios-install' (Safari needs the site on the home screen first)
 *
 * Source of truth is Notification.permission + whether a PushSubscription is registered
 * in the browser — never localStorage. Inside the Capacitor shell there is no service
 * worker, so OS permission stands in for "subscribed".
 */
export default function usePushStatus() {
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const refresh = useCallback(async () => {
    const status = pushService.supportStatus();
    if (status === 'ios-install-required') { setState('ios-install'); return; }
    if (status === 'unsupported') { setState('unsupported'); return; }
    if (pushService.isNative()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'granted') setState('subscribed');
        else if (perm.receive === 'denied') setState('denied');
        else setState('default');
      } catch { setState('default'); }
      return;
    }
    const perm = pushService.permission();
    if (perm === 'denied') { setState('denied'); return; }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub && perm === 'granted') setState('subscribed');
      else setState('default');
    } catch {
      setState('default');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    const r = await pushService.ensureSubscribed();
    setBusy(false);
    if (r?.success) {
      setState('subscribed');
    } else if (r?.reason === 'denied') {
      setState('denied');
    } else {
      // Backend misconfig (no_vapid), pushManager throw, network error — surface it so
      // the person knows to retry; this explicit button is a manual override of the
      // soft-prompt cooldown pushService keeps.
      setError(r?.reason === 'no_vapid'
        ? 'Push not configured on the server yet. Try again in a few minutes.'
        : (r?.error || 'Could not enable push. Try again.'));
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    await pushService.unsubscribe();
    setBusy(false);
    setTestResult(null);
    setState('default');
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setTestResult(null);
    const r = await pushService.sendTest();
    setBusy(false);
    if (r?.success) {
      const d = r.data?.delivered ?? 0;
      const s = r.data?.subscriptions ?? 0;
      setTestResult({ ok: true, text: `Sent to ${d}/${s} device${s === 1 ? '' : 's'} — check your phone.` });
    } else {
      setTestResult({ ok: false, text: r?.error || 'Test push failed.' });
    }
  }, []);

  return { state, busy, error, testResult, subscribe, unsubscribe, sendTest, refresh };
}
